// Exact source textarea behavior for the three new NP200 multiline products.
// Values are normalized during capture, before the native POD input handler.
export const npPodMultilineRules = Object.freeze({
  '8900296736967': Object.freeze({ id: 'layer-763002', maxLength: 20, maxLines: 2, charsPerLine: 10 }),
  '8868543594695': Object.freeze({ id: 'layer-790003', maxLength: 16, maxLines: 2, charsPerLine: 8 }),
  '15412773617863': Object.freeze({ id: 'layer-839004', maxLength: 12, maxLines: 6, charsPerLine: 6, uppercase: true }),
});

// Match the source's grapheme-based emoji exclusion while retaining ordinary
// digits, punctuation, accented letters and non-Latin text. Its English-only
// help text does not establish an ASCII-only input restriction.
const emojiAtom = String.raw`\p{Emoji}(?:\p{Emoji_Modifier}|[\u{E0020}-\u{E007E}]+\u{E007F}|\uFE0F?\u20E3?)`;
const emoji = new RegExp(String.raw`\p{Regional_Indicator}{2}|(?![#*\d](?!\uFE0F?\u20E3))${emojiAtom}(?:\u200D${emojiAtom})*`, 'u');

export function normalizePodMultiline(input, rule) {
  let value = String(input);
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    const segments = new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value);
    value = Array.from(segments, item => item.segment).filter(segment => !emoji.test(segment)).join('');
  }
  const width = rule.charsPerLine;
  if (width) {
    // Wrap at the source component's whitespace boundaries first. Explicit
    // empty lines count toward maxLines and are preserved within that limit.
    value = value.replace(new RegExp('(?![^\\n]{1,' + width + '}$)([^\\n]{1,' + width + '})\\s', 'g'), '$1\n');
    const lines = value.split('\n');
    const last = lines.length - 1;
    if (rule.maxLines > 1 && lines[last] && lines[last].length > width) {
      const tail = lines[last];
      lines[last] = tail.slice(0, width);
      lines.push(tail.slice(width));
    }
    for (let index = 0; index < lines.length; index += 1) lines[index] = lines[index].slice(0, width);
    if (rule.maxLines > 0) lines.length = Math.min(lines.length, rule.maxLines);
    value = lines.join('\n');
  }
  if (rule.maxLength) value = value.slice(0, rule.maxLength);
  return rule.uppercase ? value.toUpperCase() : value;
}

function bindPodMultiline() {
  const loader = document.querySelector('script[data-np-pod-input-constraints]');
  const rule = npPodMultilineRules[loader?.dataset.npPodSource];
  if (!rule || document.documentElement.dataset.npPodInputConstraintsBound) return;
  document.documentElement.dataset.npPodInputConstraintsBound = 'true';
  const target = element => element instanceof HTMLTextAreaElement && element.id === rule.id && !!element.closest('#tee-artwork-form');
  const configure = element => {
    // The source sets this DOM limit on focus, then applies its separate total
    // length rule after wrapping. Keep the native POD model's total limit too.
    const limit = rule.maxLines * rule.charsPerLine;
    if (element.maxLength !== limit) element.maxLength = limit;
  };
  document.addEventListener('focusin', event => {
    if (target(event.target)) configure(event.target);
  }, { capture: true });
  document.addEventListener('input', event => {
    const element = event.target;
    if (!target(element) || event.isComposing) return;
    configure(element);
    const previous = element.value;
    const value = normalizePodMultiline(previous, rule);
    if (value !== previous) {
      element.value = value;
      // Match the source's direct value assignment. Keeping the old cursor
      // offset after inserting a wrap would place it before the last letter
      // and reverse subsequent typing.
    }
    // Do not dispatch a second input: the original event continues to the
    // native handler with the corrected value and updates its linked artwork.
  }, { capture: true });
  // Delegated focus/input also covers controls mounted or replaced later by
  // the SDK, without a global mutation observer or repeated initialization.
  const element = document.getElementById(rule.id);
  if (target(element)) configure(element);
}

if (typeof document !== 'undefined') bindPodMultiline();
